import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import { budgetService } from '../../services/budgetService';
import { getCurrencySymbol } from '../../utils/currencyFormatter';

import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from 'recharts';
import { Plus, Trash2, Save, CheckCircle, Wallet, TrendingUp, Activity, Settings, DollarSign, TrendingDown, AlertCircle, Users, Edit2 } from 'lucide-react';

export default function BusinessBudgetManagerAnalyticsDashboard() {
  const { user, profile } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  
  // UPDATED: Add month selector UI state
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}`;
  });
  
  // Data states
  const [categories, setCategories] = useState([]);
  const [records, setRecords] = useState([]);
  
  // Computed metrics
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    budgetedSavings: 0,
    netProfit: 0,
    disposableCash: 0,
    expensePercent: 0,
    savingsPercent: 0,
    netProfitMargin: 0
  });

  // CRITICAL FIX: Remove mock employee data - start with empty array
  // All employee data should come from database only
  const [employees, setEmployees] = useState([]);
  
  // Account Info states (moved from Settings)
  const [accountInfo, setAccountInfo] = useState({
    businessName: 'My Business',
    currency: 'R' // Fallback for loading state
  });

  // Settings tab states (simplified)
  const [budgetSettings, setBudgetSettings] = useState({
    savingsTarget: 15,
    annualSalaryIncrease: 5,
    showDebugTools: false
  });

  // UI states
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ name: '', salary: 0 });
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [newIncomeForm, setNewIncomeForm] = useState({ name: '', amount: 0, description: '' });
  const [newExpenseForm, setNewExpenseForm] = useState({ name: '', amount: 0, description: '' });
  const [showAddSaving, setShowAddSaving] = useState(false);
  const [newSavingForm, setNewSavingForm] = useState({ name: '', amount: 0, description: '', type: 'savings' });
  const [showAddAverageIncome, setShowAddAverageIncome] = useState(false);
  const [newAverageIncomeForm, setNewAverageIncomeForm] = useState({ 
    name: '', 
    averageAmount: 0, 
    description: '', 
    notes: 'Variable income - estimate based on average' 
  });
  
  // ADD: Debug view state for showing all income records
  const [showAllIncomeRecords, setShowAllIncomeRecords] = useState(false);
  const [allIncomeRecords, setAllIncomeRecords] = useState([]);
  const [isLoadingAllRecords, setIsLoadingAllRecords] = useState(false);

  // ADD: Edit states for records
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', amount: 0, description: '', notes: '' });

  // Save states
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const saveTimeoutRef = useRef(null);

  // Helper function to get the last day of a month
  const getLastDayOfMonth = (yearMonth) => {
    const [year, month] = yearMonth?.split('-')?.map(Number);
    const lastDay = new Date(year, month, 0)?.getDate();
    return `${yearMonth}-${String(lastDay)?.padStart(2, '0')}`;
  };

  // ADD: Confirmation modal state
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    recordId: null,
    recordName: '',
    recordAmount: 0
  });

  useEffect(() => {
    if (user) {
      loadAllData();
    }
  }, [user, selectedMonth]);

  // NEW: Update currency when profile loads
  useEffect(() => {
    if (profile?.currency) {
      const currencySymbol = getCurrencySymbol(profile?.currency);
      console.log('🔍 Currency Debug:', {
        profileCurrency: profile?.currency,
        currencySymbol: currencySymbol,
        previousCurrency: accountInfo?.currency
      });
      setAccountInfo(prev => ({
        ...prev,
        currency: currencySymbol
      }));
    }
  }, [profile]);

  // Auto-save functionality
  useEffect(() => {
    if (saveTimeoutRef?.current) {
      clearTimeout(saveTimeoutRef?.current);
    }

    if (isLoading) return;

    saveTimeoutRef.current = setTimeout(() => {
      handleAutoSave();
    }, 2000);

    return () => {
      if (saveTimeoutRef?.current) {
        clearTimeout(saveTimeoutRef?.current);
      }
    };
  }, [records, employees, accountInfo, budgetSettings]);

  const handleAutoSave = async () => {
    try {
      setIsSaving(true);
      setSaveError('');
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error auto-saving:', error);
      setSaveError('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleManualSave = async () => {
    try {
      setIsSaving(true);
      setSaveError('');
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving:', error);
      setSaveError('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const loadAllData = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const endDate = getLastDayOfMonth(selectedMonth);
      
      // Load filtered records for current month only
      const [categoriesData, recordsData] = await Promise.all([
        budgetService?.getCategories(user?.id),
        budgetService?.getRecords(user?.id, { 
          startDate: `${selectedMonth}-01`,
          endDate: endDate
        })
      ]);

      setCategories(categoriesData);
      setRecords(recordsData);

      calculateMetrics(recordsData);
    } catch (err) {
      setError(err?.message);
      console.error('Error loading budget data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateMetrics = (recordsData) => {
    // CRITICAL FIX: Ensure savings are NEVER counted as income
    // Only actual income streams should count towards revenue
    const actualIncome = recordsData
      ?.filter(r => 
        r?.budgetCategories?.categoryType === 'income' && !r?.budgetCategories?.name?.toLowerCase()?.includes('saving') &&
        !r?.budgetCategories?.name?.toLowerCase()?.includes('reserve')
      )
      ?.reduce((sum, r) => sum + parseFloat(r?.amount || 0), 0);
    
    const expenses = recordsData
      ?.filter(r => r?.budgetCategories?.categoryType === 'expense')
      ?.reduce((sum, r) => sum + parseFloat(r?.amount || 0), 0);
    
    // CRITICAL FIX: Get actual savings - these should NEVER be part of revenue
    const savingsRecords = recordsData?.filter(r => 
      (r?.budgetCategories?.name?.toLowerCase()?.includes('saving') ||
       r?.budgetCategories?.name?.toLowerCase()?.includes('reserve'))
    );
    
    const actualSavings = savingsRecords?.reduce((sum, r) => sum + parseFloat(r?.amount || 0), 0);
    
    // CRITICAL FIX: Calculate net profit FIRST (revenue - expenses only)
    const netProfit = actualIncome - expenses;
    
    // CRITICAL FIX: Only use actual savings from records, NOT calculated target
    // If user hasn't added any savings, show 0 - don't auto-calculate
    const savings = actualSavings; // Remove fallback to percentage calculation
    const disposable = netProfit - savings;

    setMetrics({
      totalRevenue: actualIncome,
      totalExpenses: expenses,
      budgetedSavings: savings, // Will be 0 if no savings added
      netProfit: netProfit,
      disposableCash: disposable,
      expensePercent: actualIncome > 0 ? (expenses / actualIncome) * 100 : 0,
      savingsPercent: actualIncome > 0 ? (savings / actualIncome) * 100 : 0,
      netProfitMargin: actualIncome > 0 ? (netProfit / actualIncome) * 100 : 0
    });
  };

  // Employee Management Functions
  const handleAddEmployee = async () => {
    if (!newEmployee?.name || !newEmployee?.salary || newEmployee?.salary <= 0) return;
    
    try {
      // CRITICAL FIX: Create salary as an expense category and record
      let salaryCategory = categories?.find(c => 
        c?.categoryType === 'expense' && c?.name?.toLowerCase()?.includes('salary') &&
        c?.name?.toLowerCase()?.includes(newEmployee?.name?.toLowerCase())
      );
      
      if (!salaryCategory) {
        salaryCategory = await budgetService?.createCategory({
          name: `Salary - ${newEmployee?.name}`,
          categoryType: 'expense',
          description: `Monthly salary for ${newEmployee?.name}`,
          color: '#8b5cf6', // Purple color for salaries
          budgetAmount: 0
        });
      }

      // Create expense record for the salary
      await budgetService?.createRecord({
        categoryId: salaryCategory?.id,
        amount: parseFloat(newEmployee?.salary),
        description: `Monthly salary for ${newEmployee?.name}`,
        transactionDate: `${selectedMonth}-15`,
        frequency: 'monthly',
        notes: 'salary_expense'
      });

      setNewEmployee({ name: '', salary: 0 });
      setShowAddEmployee(false);
      loadAllData(); // Reload to show new salary in expenses
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    try {
      // Find and delete the associated salary expense record
      const salaryRecord = records?.find(r => 
        r?.notes === 'salary_expense' && 
        r?.id === employeeId
      );
      
      if (salaryRecord) {
        await budgetService?.deleteRecord(salaryRecord?.id);
      }
      
      loadAllData(); // Reload to update expenses
    } catch (err) {
      setError(err?.message);
    }
  };

  const calculateEmployeeNewSalary = (employee) => {
    const currentSalary = parseFloat(employee?.amount || 0);
    // Increase is now tracked in database, no need to calculate
    return currentSalary;
  };

  const calculateTotalSalaryCost = () => {
    // Get all salary expense records from database
    const salaryRecords = records?.filter(r => 
      r?.notes === 'salary_expense' &&
      r?.budgetCategories?.categoryType === 'expense'
    );
    
    return salaryRecords?.reduce((sum, record) => 
      sum + parseFloat(record?.amount || 0), 0
    );
  };

  // ADD: Helper function to get salary expense records
  const getSalaryExpenseRecords = () => {
    return records?.filter(r => 
      r?.notes === 'salary_expense' &&
      r?.budgetCategories?.categoryType === 'expense'
    );
  };

  // ADD: Edit functionality for all records
  const handleStartEdit = (record) => {
    setEditingRecord(record?.id);
    setEditForm({
      name: record?.budgetCategories?.name || '',
      amount: record?.amount || 0,
      description: record?.description || '',
      notes: record?.notes || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingRecord(null);
    setEditForm({ name: '', amount: 0, description: '', notes: '' });
  };

  const handleSaveEdit = async (recordId) => {
    if (!editForm?.amount || editForm?.amount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }
    
    try {
      await budgetService?.updateRecord(recordId, {
        amount: parseFloat(editForm?.amount),
        description: editForm?.description || '',
        notes: editForm?.notes || ''
      });

      setEditingRecord(null);
      setEditForm({ name: '', amount: 0, description: '', notes: '' });
      loadAllData();
    } catch (err) {
      setError(err?.message);
    }
  };

  // Income/Expense Management
  const handleAddIncome = async () => {
    if (!newIncomeForm?.name || newIncomeForm?.amount <= 0) return;
    
    try {
      // Find or create income category
      let incomeCategory = categories?.find(c => c?.categoryType === 'income' && c?.name === newIncomeForm?.name);
      
      if (!incomeCategory) {
        incomeCategory = await budgetService?.createCategory({
          name: newIncomeForm?.name,
          categoryType: 'income',
          description: newIncomeForm?.description || newIncomeForm?.name,
          color: '#10b981',
          budgetAmount: 0
        });
      }

      // CRITICAL FIX: Set frequency='monthly' and notes='recurring' to identify recurring income
      await budgetService?.createRecord({
        categoryId: incomeCategory?.id,
        amount: parseFloat(newIncomeForm?.amount),
        description: newIncomeForm?.description || newIncomeForm?.name,
        transactionDate: `${selectedMonth}-15`,
        frequency: 'monthly',
        notes: 'recurring'
      });

      setNewIncomeForm({ name: '', amount: 0, description: '' });
      setShowAddIncome(false);
      loadAllData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleAddExpense = async () => {
    if (!newExpenseForm?.name || newExpenseForm?.amount <= 0) return;
    
    try {
      let expenseCategory = categories?.find(c => c?.categoryType === 'expense' && c?.name === newExpenseForm?.name);
      
      if (!expenseCategory) {
        expenseCategory = await budgetService?.createCategory({
          name: newExpenseForm?.name,
          categoryType: 'expense',
          description: newExpenseForm?.description || newExpenseForm?.name,
          color: '#ef4444',
          budgetAmount: 0
        });
      }

      await budgetService?.createRecord({
        categoryId: expenseCategory?.id,
        amount: parseFloat(newExpenseForm?.amount),
        description: newExpenseForm?.description || newExpenseForm?.name,
        transactionDate: `${selectedMonth}-15`
      });

      setNewExpenseForm({ name: '', amount: 0, description: '' });
      setShowAddExpense(false);
      loadAllData();
    } catch (err) {
      setError(err?.message);
    }
  };

  // Add new handlers for Savings
  const handleAddSaving = async () => {
    if (!newSavingForm?.name || newSavingForm?.amount <= 0) return;
    
    try {
      let savingCategory = categories?.find(c => 
        c?.categoryType === 'income' && 
        c?.name === newSavingForm?.name &&
        c?.name?.toLowerCase()?.includes('saving')
      );
      
      if (!savingCategory) {
        savingCategory = await budgetService?.createCategory({
          name: newSavingForm?.name,
          categoryType: 'income',
          description: newSavingForm?.description || `Savings: ${newSavingForm?.name}`,
          color: '#3b82f6',
          budgetAmount: 0
        });
      }

      await budgetService?.createRecord({
        categoryId: savingCategory?.id,
        amount: parseFloat(newSavingForm?.amount),
        description: newSavingForm?.description || newSavingForm?.name,
        transactionDate: `${selectedMonth}-15`,
        frequency: 'monthly'
      });

      setNewSavingForm({ name: '', amount: 0, description: '', type: 'savings' });
      setShowAddSaving(false);
      loadAllData();
    } catch (err) {
      setError(err?.message);
    }
  };

  // Add new handlers for Average Income
  const handleAddAverageIncome = async () => {
    if (!newAverageIncomeForm?.name || newAverageIncomeForm?.averageAmount <= 0) return;
    
    try {
      let averageIncomeCategory = categories?.find(c => 
        c?.categoryType === 'income' && 
        c?.name === newAverageIncomeForm?.name
      );
      
      if (!averageIncomeCategory) {
        averageIncomeCategory = await budgetService?.createCategory({
          name: newAverageIncomeForm?.name,
          categoryType: 'income',
          description: `${newAverageIncomeForm?.description} (Variable/Average Income)`,
          color: '#10b981',
          budgetAmount: 0
        });
      }

      // CRITICAL FIX: Set frequency='variable' instead of 'monthly' to properly identify average income
      await budgetService?.createRecord({
        categoryId: averageIncomeCategory?.id,
        amount: parseFloat(newAverageIncomeForm?.averageAmount),
        description: newAverageIncomeForm?.description || newAverageIncomeForm?.name,
        notes: newAverageIncomeForm?.notes,
        transactionDate: `${selectedMonth}-15`,
        frequency: 'variable'
      });

      setNewAverageIncomeForm({ 
        name: '', 
        averageAmount: 0, 
        description: '', 
        notes: 'Variable income - estimate based on average' 
      });
      setShowAddAverageIncome(false);
      loadAllData();
    } catch (err) {
      setError(err?.message);
    }
  };

  // UPDATED: Smooth deletion with confirmation modal
  const handleDeleteRecord = async (recordId) => {
    try {
      // Close confirmation modal
      setDeleteConfirmation({ show: false, recordId: null, recordName: '', recordAmount: 0 });

      // Optimistically update UI by removing the record from state
      setRecords(prevRecords => prevRecords?.filter(r => r?.id !== recordId));

      // Delete from database
      await budgetService?.deleteRecord(recordId);

      // Recalculate metrics with updated records
      const updatedRecords = records?.filter(r => r?.id !== recordId);
      calculateMetrics(updatedRecords);
    } catch (err) {
      setError(err?.message);
      // Reload data if deletion failed to restore correct state
      loadAllData();
    }
  };

  // ADD: Function to show delete confirmation
  const showDeleteConfirmation = (record) => {
    setDeleteConfirmation({
      show: true,
      recordId: record?.id,
      recordName: record?.budgetCategories?.name,
      recordAmount: parseFloat(record?.amount)
    });
  };

  // ADD: Function to cancel deletion
  const cancelDelete = () => {
    setDeleteConfirmation({ show: false, recordId: null, recordName: '', recordAmount: 0 });
  };

  // ADD: Function to load ALL income records (no date filter)
  const loadAllIncomeRecords = async () => {
    try {
      setIsLoadingAllRecords(true);
      // Fetch all records without date filter
      const allRecordsData = await budgetService?.getRecords(user?.id, {});
      
      // Filter only income records
      const incomeOnly = allRecordsData?.filter(r => 
        r?.budgetCategories?.categoryType === 'income'
      );
      
      setAllIncomeRecords(incomeOnly);
    } catch (err) {
      setError(err?.message);
      console.error('Error loading all income records:', err);
    } finally {
      setIsLoadingAllRecords(false);
    }
  };

  // ADD: Function to toggle debug view
  const toggleShowAllRecords = async () => {
    if (!showAllIncomeRecords) {
      await loadAllIncomeRecords();
    }
    setShowAllIncomeRecords(!showAllIncomeRecords);
  };

  // ADD: Function to delete all income records with confirmation
  const handleDeleteAllIncomeRecords = async () => {
    if (!window.confirm(`⚠️ WARNING: This will delete ALL ${allIncomeRecords?.length} income records from your database. This action cannot be undone. Are you absolutely sure?`)) {
      return;
    }
    
    try {
      setIsLoadingAllRecords(true);
      
      // Delete all income records one by one
      for (const record of allIncomeRecords) {
        await budgetService?.deleteRecord(record?.id);
      }
      
      // Reload data
      await loadAllData();
      await loadAllIncomeRecords();
      
      alert(`✅ Successfully deleted ${allIncomeRecords?.length} income records.`);
    } catch (err) {
      setError(err?.message);
      console.error('Error deleting all income records:', err);
      alert(`❌ Error deleting records: ${err?.message}`);
    } finally {
      setIsLoadingAllRecords(false);
    }
  };

  const getIncomeRecords = () => {
    return records?.filter(r => r?.budgetCategories?.categoryType === 'income');
  };

  const getExpenseRecords = () => {
    return records?.filter(r => r?.budgetCategories?.categoryType === 'expense');
  };

  const getSavingsRecords = () => {
    return records?.filter(r => 
      (r?.budgetCategories?.name?.toLowerCase()?.includes('saving') ||
       r?.budgetCategories?.name?.toLowerCase()?.includes('reserve'))
    );
  };

  const getRecurringIncomeRecords = () => {
    // CRITICAL FIX: Look for frequency='monthly' OR notes='recurring' to catch all recurring income
    return records?.filter(r => 
      r?.budgetCategories?.categoryType === 'income' &&
      (r?.frequency === 'monthly' || r?.notes?.toLowerCase()?.includes('recurring')) &&
      !r?.budgetCategories?.name?.toLowerCase()?.includes('saving')
    );
  };

  const getAverageIncomeRecords = () => {
    // CRITICAL FIX: Look for frequency='variable' OR notes containing 'variable'/'average' to catch all average income
    return records?.filter(r => 
      r?.budgetCategories?.categoryType === 'income' && 
      (r?.frequency === 'variable'|| r?.notes?.toLowerCase()?.includes('variable') ||
       r?.notes?.toLowerCase()?.includes('average')) &&
      !r?.budgetCategories?.name?.toLowerCase()?.includes('saving')
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'income', label: 'Income', icon: TrendingUp },
    { id: 'expenses', label: 'Expenses', icon: TrendingDown },
    { id: 'savings', label: 'Savings', icon: Wallet },
    { id: 'salaries', label: 'Salaries', icon: Users },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const chartColors = {
    income: '#10b981',
    expenses: '#ef4444',
    savings: '#3b82f6',
    netProfit: '#8b5cf6'
  };

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Budget Manager | FinanceFlow</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
          <Sidebar />
          <Header />
          <main className="ml-64 mt-16 px-6 pb-6 pt-4">
            {/* Skeleton Header */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl shadow-lg border border-blue-400 p-8 mb-6 relative overflow-hidden animate-pulse">
              <div className="relative flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-8 bg-white/20 rounded-lg w-64 mb-2"></div>
                  <div className="h-4 bg-white/10 rounded-lg w-96"></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-10 bg-white/20 rounded-xl w-40"></div>
                  <div className="h-10 bg-white/20 rounded-xl w-32"></div>
                </div>
              </div>
            </div>

            {/* Skeleton Tab Navigation */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 mb-6 overflow-hidden animate-pulse">
              <div className="flex border-b border-slate-200/50">
                {[1, 2, 3, 4, 5, 6]?.map((i) => (
                  <div key={i} className="px-6 py-4 flex items-center gap-2.5">
                    <div className="w-5 h-5 bg-slate-200 rounded"></div>
                    <div className="h-4 bg-slate-200 rounded w-20"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Skeleton Metric Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 animate-pulse">
              {[1, 2, 3, 4, 5]?.map((i) => (
                <div key={i} className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="h-4 bg-slate-200 rounded w-24"></div>
                    <div className="w-10 h-10 bg-slate-200 rounded-xl"></div>
                  </div>
                  <div className="h-8 bg-slate-200 rounded w-32 mb-2"></div>
                  <div className="h-3 bg-slate-200 rounded w-40"></div>
                </div>
              ))}
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Budget Manager | FinanceFlow</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <Sidebar />
        <Header />
        <main className="ml-64 mt-16 px-6 pb-6 pt-4">
          {/* Page Header - UPDATED with gradient */}
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl shadow-lg border border-blue-400 p-8 mb-6 relative overflow-hidden">
            {/* Decorative gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none"></div>
            
            <div className="relative flex items-center justify-between">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-2 drop-shadow-md">Budget Manager</h1>
                <p className="text-blue-100 text-base">
                  Simple, powerful budget planning for your business
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Save Status */}
                <div className="flex items-center gap-2 px-4 py-2.5 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 shadow-lg">
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span className="text-sm text-white font-medium">Saving...</span>
                    </>
                  ) : lastSaved ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-300" />
                      <span className="text-sm text-white font-medium">
                        Saved {new Date(lastSaved)?.toLocaleTimeString()}
                      </span>
                    </>
                  ) : null}
                </div>

                {/* Manual Save Button - UPDATED */}
                <button
                  onClick={handleManualSave}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-white text-blue-600 rounded-xl hover:bg-blue-50 transition-all duration-200 text-sm font-semibold shadow-lg disabled:opacity-50 hover:shadow-xl hover:scale-105"
                >
                  <Save className="w-5 h-5" />
                  Save Budget
                </button>
              </div>
            </div>

            {/* Error Message */}
            {(saveError || error) && (
              <div className="mt-4 px-4 py-3 bg-red-500/90 backdrop-blur-sm border border-red-400 rounded-xl flex items-center gap-2 shadow-lg">
                <AlertCircle className="w-5 h-5 text-white flex-shrink-0" />
                <p className="text-sm text-white font-medium">{saveError || error}</p>
              </div>
            )}
          </div>

          {/* ADD: Delete Confirmation Modal */}
          {deleteConfirmation?.show && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900 mb-2">Delete Record?</h3>
                    <p className="text-sm text-slate-600 mb-4">
                      Are you sure you want to delete <span className="font-semibold">{deleteConfirmation?.recordName}</span> ({accountInfo?.currency}{deleteConfirmation?.recordAmount?.toLocaleString()})?
                      This action cannot be undone.
                    </p>
                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={cancelDelete}
                        className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors text-sm font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDeleteRecord(deleteConfirmation?.recordId)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab Navigation - UPDATED with modern styling */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 mb-6 overflow-hidden">
            <div className="flex border-b border-slate-200/50">
              {tabs?.map((tab) => {
                const TabIcon = tab?.icon;
                return (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2.5 px-6 py-4 text-sm font-semibold transition-all duration-200 relative ${
                      activeTab === tab?.id
                        ? 'text-blue-600 bg-blue-50/50' :'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    <TabIcon className={`w-5 h-5 ${activeTab === tab?.id ? 'animate-pulse' : ''}`} />
                    {tab?.label}
                    {activeTab === tab?.id && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Top Metric Cards - ENHANCED with better shadows and animations */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {/* Total Revenue */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl shadow-lg hover:shadow-xl border border-green-200 p-6 transition-all duration-300 hover:scale-105">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-green-900">Total Revenue</h3>
                    <div className="p-2 bg-green-200 rounded-xl">
                      <DollarSign className="w-5 h-5 text-green-700" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-green-900 mb-1">
                    {accountInfo?.currency}{metrics?.totalRevenue?.toLocaleString()}
                  </p>
                  <p className="text-xs text-green-700 font-medium">
                    💡 All income streams combined
                  </p>
                </div>

                {/* Total Expenses */}
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl shadow-lg hover:shadow-xl border border-red-200 p-6 transition-all duration-300 hover:scale-105">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-red-900">Operating Expenses</h3>
                    <div className="p-2 bg-red-200 rounded-xl">
                      <TrendingDown className="w-5 h-5 text-red-700" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-red-900 mb-1">
                    {accountInfo?.currency}{metrics?.totalExpenses?.toLocaleString()}
                  </p>
                  <p className="text-xs text-red-700 font-medium">
                    {metrics?.expensePercent?.toFixed(0)}% of revenue
                  </p>
                </div>

                {/* Savings */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-lg hover:shadow-xl border border-blue-200 p-6 transition-all duration-300 hover:scale-105">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-blue-900">Planned Savings</h3>
                    <div className="p-2 bg-blue-200 rounded-xl">
                      <Wallet className="w-5 h-5 text-blue-700" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-blue-900 mb-1">
                    {accountInfo?.currency}{metrics?.budgetedSavings?.toLocaleString()}
                  </p>
                  <p className="text-xs text-blue-700 font-medium">
                    💡 Set aside from net profit
                  </p>
                </div>

                {/* Net Profit */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl shadow-lg hover:shadow-xl border border-purple-200 p-6 transition-all duration-300 hover:scale-105">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-purple-900">Net Profit</h3>
                    <div className="p-2 bg-purple-200 rounded-xl">
                      <TrendingUp className="w-5 h-5 text-purple-700" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-purple-900 mb-1">
                    {accountInfo?.currency}{metrics?.netProfit?.toLocaleString()}
                  </p>
                  <p className="text-xs text-purple-700 font-medium">
                    {metrics?.netProfitMargin?.toFixed(1)}% margin
                  </p>
                </div>

                {/* Disposable Cash */}
                <div className={`bg-gradient-to-br rounded-2xl shadow-lg hover:shadow-xl border p-6 transition-all duration-300 hover:scale-105 ${
                  metrics?.disposableCash >= 0 
                    ? 'from-emerald-50 to-emerald-100 border-emerald-200' :'from-yellow-50 to-yellow-100 border-yellow-200'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`text-sm font-bold ${
                      metrics?.disposableCash >= 0 ? 'text-emerald-900' : 'text-yellow-900'
                    }`}>
                      Disposable Cash
                    </h3>
                    <div className={`p-2 rounded-xl ${
                      metrics?.disposableCash >= 0 ? 'bg-emerald-200' : 'bg-yellow-200'
                    }`}>
                      <DollarSign className={`w-5 h-5 ${
                        metrics?.disposableCash >= 0 ? 'text-emerald-700' : 'text-yellow-700'
                      }`} />
                    </div>
                  </div>
                  <p className={`text-3xl font-bold mb-1 ${
                    metrics?.disposableCash >= 0 ? 'text-emerald-900' : 'text-yellow-900'
                  }`}>
                    {accountInfo?.currency}{metrics?.disposableCash?.toLocaleString()}
                  </p>
                  <p className={`text-xs font-medium ${
                    metrics?.disposableCash >= 0 ? 'text-emerald-700' : 'text-yellow-700'
                  }`}>
                    {metrics?.disposableCash >= 0 
                      ? '✨ Available for bonuses & extras' 
                      : '⚠️ Consider reducing expenses'}
                  </p>
                </div>
              </div>

              {/* Charts Row - ENHANCED */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Budget Distribution Pie Chart */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200 p-6 hover:shadow-xl transition-all duration-300">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Activity className="w-5 h-5 text-blue-600" />
                    </div>
                    Budget Distribution
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Revenue', value: metrics?.totalRevenue, color: chartColors?.income },
                          { name: 'Expenses', value: metrics?.totalExpenses, color: chartColors?.expenses },
                          { name: 'Savings', value: metrics?.budgetedSavings, color: chartColors?.savings },
                          { name: 'Net Profit', value: Math.max(0, metrics?.netProfit), color: chartColors?.netProfit }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {[chartColors?.income, chartColors?.expenses, chartColors?.savings, chartColors?.netProfit]?.map((color, index) => (
                          <Cell key={`cell-${index}`} fill={color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => `${accountInfo?.currency}${value?.toLocaleString()}`}
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* NEW: Salary vs Other Expenses Chart */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200 p-6 hover:shadow-xl transition-all duration-300">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                    Expense Breakdown: Salaries vs Others
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { 
                            name: 'Salaries', 
                            value: calculateTotalSalaryCost(), 
                            color: '#8b5cf6' 
                          },
                          { 
                            name: 'Other Expenses', 
                            value: metrics?.totalExpenses - calculateTotalSalaryCost(), 
                            color: '#ef4444' 
                          }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={3}
                        dataKey="value"
                        label={({name, percent}) => `${name}: ${(percent * 100)?.toFixed(1)}%`}
                      >
                        <Cell fill="#8b5cf6" />
                        <Cell fill="#ef4444" />
                      </Pie>
                      <Tooltip 
                        formatter={(value) => `${accountInfo?.currency}${value?.toLocaleString()}`}
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                      <p className="text-xs font-semibold text-purple-900 mb-1">Total Salaries</p>
                      <p className="text-lg font-bold text-purple-700">
                        {accountInfo?.currency}{calculateTotalSalaryCost()?.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                      <p className="text-xs font-semibold text-red-900 mb-1">Other Expenses</p>
                      <p className="text-lg font-bold text-red-700">
                        {accountInfo?.currency}{(metrics?.totalExpenses - calculateTotalSalaryCost())?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Budget Health Tips - ENHANCED */}
              <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl shadow-lg border border-blue-200 p-6 hover:shadow-xl transition-all duration-300">
                <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-200 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-blue-700" />
                  </div>
                  Budget Health Tips
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border border-blue-100 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105">
                    <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                      <span className="text-lg">💰</span> Operating Expenses
                    </h4>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {metrics?.expensePercent < 70 
                        ? '✅ Excellent! Operating costs are well controlled.' 
                        : metrics?.expensePercent < 85 
                          ? '👍 Good! Consider optimizing further.' :'⚠️ High! Review and reduce operating costs.'}
                    </p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border border-blue-100 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105">
                    <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                      <span className="text-lg">📊</span> Savings Strategy
                    </h4>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {metrics?.savingsPercent >= 15 
                        ? `✅ Great! Saving ${metrics?.savingsPercent?.toFixed(0)}% of revenue.` 
                        : metrics?.savingsPercent >= 10 
                          ? '👍 Good start! Aim for 15% savings rate.' 
                          : '💡 Try to save at least 10% of revenue.'}
                    </p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border border-blue-100 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105">
                    <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                      <span className="text-lg">🎯</span> Disposable Cash
                    </h4>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {metrics?.disposableCash > 0 
                        ? `✅ ${accountInfo?.currency}${metrics?.disposableCash?.toLocaleString()} available after savings & expenses.` 
                        : '⚠️ All profit allocated. Adjust savings or expenses if bonuses needed.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* INCOME TAB - ENHANCED */}
          {activeTab === 'income' && (
            <div className="space-y-6">
              {/* Add Income Buttons - ENHANCED */}
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <TrendingUp className="w-7 h-7 text-green-600" />
                    Income Sources
                  </h2>
                  <p className="text-sm text-slate-600 mt-1 ml-9">Track recurring and variable income</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowAddIncome(true)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 text-sm font-semibold shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    <Plus className="w-5 h-5" />
                    Add Recurring Income
                  </button>
                  <button
                    onClick={() => setShowAddAverageIncome(true)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl hover:from-teal-700 hover:to-teal-800 transition-all duration-200 text-sm font-semibold shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    <Plus className="w-5 h-5" />
                    Add Average Income
                  </button>
                </div>
              </div>

              {/* ADD: Debug Button - Show All Income Records */}
              {budgetSettings?.showDebugTools && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-amber-900 mb-1">🔍 Debug: View All Income Records</h3>
                      <p className="text-sm text-amber-700">
                        See ALL income records across ALL months (including empty/phantom records) with bulk delete option
                      </p>
                    </div>
                    <button
                      onClick={toggleShowAllRecords}
                      disabled={isLoadingAllRecords}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium disabled:opacity-50"
                    >
                      {isLoadingAllRecords ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Loading...
                        </>
                      ) : showAllIncomeRecords ? (
                        'Hide All Records'
                      ) : (
                        'Show All Income Records'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* ADD: All Income Records Debug View */}
              {showAllIncomeRecords && (
                <div className="bg-white rounded-lg shadow-sm border-2 border-amber-300 overflow-hidden">
                  <div className="bg-amber-50 px-6 py-3 border-b border-amber-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-amber-900">All Income Records (Unfiltered)</h3>
                        <p className="text-xs text-amber-700 mt-1">
                          Showing {allIncomeRecords?.length} total income records across ALL months
                        </p>
                      </div>
                      {allIncomeRecords?.length > 0 && (
                        <button
                          onClick={handleDeleteAllIncomeRecords}
                          disabled={isLoadingAllRecords}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete All Income Records
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-amber-100 border-b border-amber-200 sticky top-0">
                        <tr>
                          <th className="text-left px-6 py-3 text-xs font-bold text-amber-900">ID</th>
                          <th className="text-left px-6 py-3 text-xs font-bold text-amber-900">Category</th>
                          <th className="text-left px-6 py-3 text-xs font-bold text-amber-900">Description</th>
                          <th className="text-right px-6 py-3 text-xs font-bold text-amber-900">Amount</th>
                          <th className="text-center px-6 py-3 text-xs font-bold text-amber-900">Date</th>
                          <th className="text-center px-6 py-3 text-xs font-bold text-amber-900">Frequency</th>
                          <th className="text-left px-6 py-3 text-xs font-bold text-amber-900">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allIncomeRecords?.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-6 py-8 text-center text-slate-500">
                              No income records found in database
                            </td>
                          </tr>
                        ) : (
                          allIncomeRecords?.map((record) => (
                            <tr key={record?.id} className="border-b border-amber-100 hover:bg-amber-50">
                              <td className="px-6 py-3 text-xs text-slate-600 font-mono">
                                {record?.id?.substring(0, 8)}...
                              </td>
                              <td className="px-6 py-3 text-sm font-semibold text-slate-900">
                                {record?.budgetCategories?.name}
                              </td>
                              <td className="px-6 py-3 text-sm text-slate-600">
                                {record?.description || '-'}
                              </td>
                              <td className="px-6 py-3 text-right text-sm font-bold text-green-700">
                                {accountInfo?.currency}{parseFloat(record?.amount || 0)?.toLocaleString()}
                              </td>
                              <td className="px-6 py-3 text-center text-xs text-slate-600">
                                {record?.transactionDate}
                              </td>
                              <td className="px-6 py-3 text-center">
                                <span className={`text-xs px-2 py-1 rounded ${
                                  record?.frequency === 'monthly' ?'bg-green-100 text-green-700'
                                    : record?.frequency === 'variable' ?'bg-teal-100 text-teal-700' :'bg-slate-100 text-slate-700'
                                }`}>
                                  {record?.frequency || 'none'}
                                </span>
                              </td>
                              <td className="px-6 py-3 text-xs text-slate-600">
                                {record?.notes || '-'}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Add Recurring Income Form - ENHANCED */}
              {showAddIncome && (
                <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-2xl p-6 shadow-lg">
                  <h3 className="font-bold text-green-900 mb-2 text-lg">Add New Recurring Income</h3>
                  <p className="text-sm text-green-700 mb-4 font-medium">For income that is consistent each month (salaries, subscriptions, etc.)</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      type="text"
                      placeholder="Income Source (e.g., Client Retainer)"
                      value={newIncomeForm?.name}
                      onChange={(e) => setNewIncomeForm({ ...newIncomeForm, name: e?.target?.value })}
                      className="px-4 py-2.5 border-2 border-green-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 font-medium"
                    />
                    <input
                      type="number"
                      placeholder="Monthly Amount"
                      value={newIncomeForm?.amount || ''}
                      onChange={(e) => setNewIncomeForm({ ...newIncomeForm, amount: e?.target?.value })}
                      className="px-4 py-2.5 border-2 border-green-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 font-medium"
                    />
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={newIncomeForm?.description}
                      onChange={(e) => setNewIncomeForm({ ...newIncomeForm, description: e?.target?.value })}
                      className="px-4 py-2.5 border-2 border-green-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 font-medium"
                    />
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={handleAddIncome}
                      className="px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all duration-200 font-semibold shadow-md hover:shadow-lg hover:scale-105"
                    >
                      Add Recurring Income
                    </button>
                    <button
                      onClick={() => setShowAddIncome(false)}
                      className="px-5 py-2.5 bg-white text-slate-700 rounded-xl hover:bg-slate-100 transition-all duration-200 font-semibold border-2 border-slate-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Add Average Income Form - ENHANCED */}
              {showAddAverageIncome && (
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 border-2 border-teal-300 rounded-2xl p-6 shadow-lg">
                  <h3 className="font-bold text-teal-900 mb-2 text-lg">Add Average/Variable Income</h3>
                  <p className="text-sm text-teal-700 mb-4 font-medium">For income that varies each month (sales, commissions, project work)</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      type="text"
                      placeholder="Income Source (e.g., Product Sales)"
                      value={newAverageIncomeForm?.name}
                      onChange={(e) => setNewAverageIncomeForm({ ...newAverageIncomeForm, name: e?.target?.value })}
                      className="px-4 py-2.5 border-2 border-teal-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 font-medium"
                    />
                    <input
                      type="number"
                      placeholder="Average Monthly Amount"
                      value={newAverageIncomeForm?.averageAmount || ''}
                      onChange={(e) => setNewAverageIncomeForm({ ...newAverageIncomeForm, averageAmount: e?.target?.value })}
                      className="px-4 py-2.5 border-2 border-teal-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 font-medium"
                    />
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={newAverageIncomeForm?.description}
                      onChange={(e) => setNewAverageIncomeForm({ ...newAverageIncomeForm, description: e?.target?.value })}
                      className="px-4 py-2.5 border-2 border-teal-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 font-medium"
                    />
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={handleAddAverageIncome}
                      className="px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-200 font-semibold shadow-md hover:shadow-lg hover:scale-105"
                    >
                      Add Average Income
                    </button>
                    <button
                      onClick={() => setShowAddAverageIncome(false)}
                      className="px-5 py-2.5 bg-white text-slate-700 rounded-xl hover:bg-slate-100 transition-all duration-200 font-semibold border-2 border-slate-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Recurring Income List - ENHANCED */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b-2 border-green-200">
                  <h3 className="font-bold text-green-900 flex items-center gap-2">
                    <div className="w-6 h-6 bg-green-200 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-green-700" />
                    </div>
                    Recurring Income (Fixed Monthly)
                  </h3>
                </div>
                <table className="w-full">
                  <thead className="bg-green-50/50 border-b-2 border-green-200">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-bold text-green-900">Source</th>
                      <th className="text-left px-6 py-4 text-sm font-bold text-green-900">Description</th>
                      <th className="text-right px-6 py-4 text-sm font-bold text-green-900">Amount</th>
                      <th className="text-right px-6 py-4 text-sm font-bold text-green-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getRecurringIncomeRecords()?.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                              <TrendingUp className="w-8 h-8 text-green-600" />
                            </div>
                            <p className="text-slate-600 font-medium">No recurring income sources yet.</p>
                            <p className="text-sm text-slate-500">Add your first recurring income above!</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      getRecurringIncomeRecords()?.map((record) => (
                        <tr key={record?.id} className="border-b border-slate-100 hover:bg-green-50/30 transition-colors duration-150">
                          {editingRecord === record?.id ? (
                            <>
                              <td className="px-6 py-4 font-semibold text-slate-400">{record?.budgetCategories?.name}</td>
                              <td className="px-6 py-4">
                                <input
                                  type="text"
                                  value={editForm?.description}
                                  onChange={(e) => setEditForm({ ...editForm, description: e?.target?.value })}
                                  className="w-full px-3 py-2 border-2 border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 transition-all duration-200"
                                  placeholder="Description"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <input
                                  type="number"
                                  value={editForm?.amount}
                                  onChange={(e) => setEditForm({ ...editForm, amount: e?.target?.value })}
                                  className="w-full px-3 py-2 border-2 border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 text-right transition-all duration-200"
                                />
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => handleSaveEdit(record?.id)}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 text-sm font-semibold transition-all duration-200"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4 font-semibold text-slate-900">{record?.budgetCategories?.name}</td>
                              <td className="px-6 py-4 text-slate-600">{record?.description}</td>
                              <td className="px-6 py-4 text-right font-bold text-green-700 text-lg">
                                {accountInfo?.currency}{parseFloat(record?.amount)?.toLocaleString()}
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => handleStartEdit(record)}
                                    className="p-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:scale-110"
                                    title="Edit"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => showDeleteConfirmation(record)}
                                    className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 hover:scale-110"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Average/Variable Income List - ENHANCED */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className="bg-gradient-to-r from-teal-50 to-teal-100 px-6 py-4 border-b-2 border-teal-200">
                  <h3 className="font-bold text-teal-900 flex items-center gap-2">
                    <div className="w-6 h-6 bg-teal-200 rounded-lg flex items-center justify-center">
                      <Activity className="w-4 h-4 text-teal-700" />
                    </div>
                    Average/Variable Income
                  </h3>
                  <p className="text-xs text-teal-700 mt-1 ml-8 font-medium">Estimated based on historical averages</p>
                </div>
                <table className="w-full">
                  <thead className="bg-teal-50/50 border-b-2 border-teal-200">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-bold text-teal-900">Source</th>
                      <th className="text-left px-6 py-4 text-sm font-bold text-teal-900">Description</th>
                      <th className="text-right px-6 py-4 text-sm font-bold text-teal-900">Avg Amount</th>
                      <th className="text-right px-6 py-4 text-sm font-bold text-teal-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getAverageIncomeRecords()?.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
                              <Activity className="w-8 h-8 text-teal-600" />
                            </div>
                            <p className="text-slate-600 font-medium">No variable income sources yet.</p>
                            <p className="text-sm text-slate-500">Add your first average income above!</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      getAverageIncomeRecords()?.map((record) => (
                        <tr key={record?.id} className="border-b border-slate-100 hover:bg-teal-50/30 transition-colors duration-150">
                          {editingRecord === record?.id ? (
                            <>
                              <td className="px-6 py-4 font-semibold text-slate-400">{record?.budgetCategories?.name}</td>
                              <td className="px-6 py-4">
                                <input
                                  type="text"
                                  value={editForm?.description}
                                  onChange={(e) => setEditForm({ ...editForm, description: e?.target?.value })}
                                  className="w-full px-3 py-2 border-2 border-teal-300 rounded-lg focus:ring-2 focus:ring-teal-500 transition-all duration-200"
                                  placeholder="Description"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <input
                                  type="number"
                                  value={editForm?.amount}
                                  onChange={(e) => setEditForm({ ...editForm, amount: e?.target?.value })}
                                  className="w-full px-3 py-2 border-2 border-teal-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-right transition-all duration-200"
                                />
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => handleSaveEdit(record?.id)}
                                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 text-sm font-semibold transition-all duration-200"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4 font-semibold text-slate-900 flex items-center gap-2">
                                {record?.budgetCategories?.name}
                                <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded">Variable</span>
                              </td>
                              <td className="px-6 py-4 text-slate-600">{record?.description}</td>
                              <td className="px-6 py-4 text-right font-bold text-teal-700">
                                {accountInfo?.currency}{parseFloat(record?.amount)?.toLocaleString()}
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => handleStartEdit(record)}
                                    className="p-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:scale-110"
                                    title="Edit"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => showDeleteConfirmation(record)}
                                    className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 hover:scale-110"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Total Income Summary - ENHANCED */}
              {(getRecurringIncomeRecords()?.length > 0 || getAverageIncomeRecords()?.length > 0) && (
                <div className="bg-gradient-to-br from-green-50 via-teal-50 to-blue-50 rounded-2xl shadow-lg border-2 border-green-200 p-6 hover:shadow-xl transition-all duration-300">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border-2 border-green-200 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105">
                      <h4 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                        <div className="w-6 h-6 bg-green-200 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-green-700" />
                        </div>
                        Recurring Income
                      </h4>
                      <p className="text-3xl font-bold text-green-700">
                        {accountInfo?.currency}{getRecurringIncomeRecords()?.reduce((sum, r) => sum + parseFloat(r?.amount || 0), 0)?.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-600 mt-2 font-medium">Fixed monthly income</p>
                    </div>
                    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border-2 border-teal-200 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105">
                      <h4 className="font-bold text-teal-900 mb-2 flex items-center gap-2">
                        <div className="w-6 h-6 bg-teal-200 rounded-lg flex items-center justify-center">
                          <Activity className="w-4 h-4 text-teal-700" />
                        </div>
                        Average Income
                      </h4>
                      <p className="text-3xl font-bold text-teal-700">
                        {accountInfo?.currency}{getAverageIncomeRecords()?.reduce((sum, r) => sum + parseFloat(r?.amount || 0), 0)?.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-600 mt-2 font-medium">Estimated variable income</p>
                    </div>
                    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border-2 border-blue-200 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105">
                      <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                        <div className="w-6 h-6 bg-blue-200 rounded-lg flex items-center justify-center">
                          <DollarSign className="w-4 h-4 text-blue-700" />
                        </div>
                        Total Income
                      </h4>
                      <p className="text-3xl font-bold text-blue-700">
                        {accountInfo?.currency}{metrics?.totalRevenue?.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-600 mt-2 font-medium">Combined total</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* EXPENSES TAB */}
          {activeTab === 'expenses' && (
            <div className="space-y-6">
              {/* Add Expense Button */}
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Business Expenses</h2>
                  <p className="text-sm text-slate-600 mt-1">Track all your costs and spending</p>
                </div>
                <button
                  onClick={() => setShowAddExpense(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add Expense
                </button>
              </div>

              {/* Add Expense Form */}
              {showAddExpense && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <h3 className="font-bold text-red-900 mb-4">Add New Expense</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      type="text"
                      placeholder="Expense Category (e.g., Rent, Marketing)"
                      value={newExpenseForm?.name}
                      onChange={(e) => setNewExpenseForm({ ...newExpenseForm, name: e?.target?.value })}
                      className="px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="Amount"
                      value={newExpenseForm?.amount || ''}
                      onChange={(e) => setNewExpenseForm({ ...newExpenseForm, amount: e?.target?.value })}
                      className="px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={newExpenseForm?.description}
                      onChange={(e) => setNewExpenseForm({ ...newExpenseForm, description: e?.target?.value })}
                      className="px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={handleAddExpense}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
                    >
                      Add Expense
                    </button>
                    <button
                      onClick={() => setShowAddExpense(false)}
                      className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Expense List with EDIT */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-red-50 border-b border-red-200">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-bold text-red-900">Category</th>
                      <th className="text-left px-6 py-4 text-sm font-bold text-red-900">Description</th>
                      <th className="text-right px-6 py-4 text-sm font-bold text-red-900">Amount</th>
                      <th className="text-right px-6 py-4 text-sm font-bold text-red-900">% of Revenue</th>
                      <th className="text-right px-6 py-4 text-sm font-bold text-red-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getExpenseRecords()?.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                          No expenses yet. Add your first expense above!
                        </td>
                      </tr>
                    ) : (
                      getExpenseRecords()?.map((record) => (
                        <tr key={record?.id} className="border-b border-slate-100 hover:bg-red-50">
                          {editingRecord === record?.id ? (
                            <>
                              <td className="px-6 py-4 font-semibold text-slate-400">{record?.budgetCategories?.name}</td>
                              <td className="px-6 py-4">
                                <input
                                  type="text"
                                  value={editForm?.description}
                                  onChange={(e) => setEditForm({ ...editForm, description: e?.target?.value })}
                                  className="w-full px-2 py-1 border border-red-300 rounded focus:ring-2 focus:ring-red-500"
                                  placeholder="Description"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <input
                                  type="number"
                                  value={editForm?.amount}
                                  onChange={(e) => setEditForm({ ...editForm, amount: e?.target?.value })}
                                  className="w-full px-2 py-1 border border-red-300 rounded focus:ring-2 focus:ring-red-500 text-right"
                                />
                              </td>
                              <td className="px-6 py-4 text-right text-slate-600">
                                {metrics?.totalRevenue > 0 
                                  ? ((parseFloat(editForm?.amount) / metrics?.totalRevenue) * 100)?.toFixed(1)
                                  : '0.0'}%
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => handleSaveEdit(record?.id)}
                                    className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="px-3 py-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300 text-sm"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4 font-semibold text-slate-900">{record?.budgetCategories?.name}</td>
                              <td className="px-6 py-4 text-slate-600">{record?.description}</td>
                              <td className="px-6 py-4 text-right font-bold text-red-700">
                                {accountInfo?.currency}{parseFloat(record?.amount)?.toLocaleString()}
                              </td>
                              <td className="px-6 py-4 text-right text-slate-600">
                                {metrics?.totalRevenue > 0 
                                  ? ((parseFloat(record?.amount) / metrics?.totalRevenue) * 100)?.toFixed(1)
                                  : '0.0'}%
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => handleStartEdit(record)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                    title="Edit"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => showDeleteConfirmation(record)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                    {getExpenseRecords()?.length > 0 && (
                      <tr className="bg-red-50 border-t-2 border-red-200">
                        <td className="px-6 py-4 font-bold text-red-900" colSpan={2}>TOTAL EXPENSES</td>
                        <td className="px-6 py-4 text-right font-bold text-red-900 text-xl">
                          {accountInfo?.currency}{metrics?.totalExpenses?.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-red-900">
                          {metrics?.expensePercent?.toFixed(1)}%
                        </td>
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Expense Insights */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg shadow-sm border border-red-200 p-6">
                <h3 className="text-lg font-bold text-red-900 mb-4">💡 Expense Insights</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-red-100">
                    <h4 className="font-semibold text-red-900 mb-2">Expense Ratio</h4>
                    <p className="text-2xl font-bold text-red-700 mb-1">{metrics?.expensePercent?.toFixed(1)}%</p>
                    <p className="text-sm text-slate-700">
                      {metrics?.expensePercent < 70 
                        ? 'Excellent control of expenses!' :'Consider ways to optimize spending'}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-red-100">
                    <h4 className="font-semibold text-red-900 mb-2">Biggest Expense</h4>
                    {getExpenseRecords()?.length > 0 ? (
                      <>
                        <p className="text-lg font-bold text-red-700 mb-1">
                          {getExpenseRecords()?.sort((a, b) => parseFloat(b?.amount) - parseFloat(a?.amount))?.[0]?.budgetCategories?.name}
                        </p>
                        <p className="text-sm text-slate-700">
                          {accountInfo?.currency}{parseFloat(getExpenseRecords()?.sort((a, b) => parseFloat(b?.amount) - parseFloat(a?.amount))?.[0]?.amount)?.toLocaleString()}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500">No expenses tracked yet</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* NEW SAVINGS TAB */}
          {activeTab === 'savings' && (
            <div className="space-y-6">
              {/* Add Saving Button */}
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Savings & Reserves</h2>
                  <p className="text-sm text-slate-600 mt-1">Plan your savings and emergency funds</p>
                </div>
                <button
                  onClick={() => setShowAddSaving(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add Savings Goal
                </button>
              </div>

              {/* Add Saving Form */}
              {showAddSaving && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="font-bold text-blue-900 mb-4">Add New Savings Goal</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      type="text"
                      placeholder="Savings Category (e.g., Emergency Fund)"
                      value={newSavingForm?.name}
                      onChange={(e) => setNewSavingForm({ ...newSavingForm, name: e?.target?.value })}
                      className="px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="Monthly Amount"
                      value={newSavingForm?.amount || ''}
                      onChange={(e) => setNewSavingForm({ ...newSavingForm, amount: e?.target?.value })}
                      className="px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={newSavingForm?.description}
                      onChange={(e) => setNewSavingForm({ ...newSavingForm, description: e?.target?.value })}
                      className="px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={handleAddSaving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                    >
                      Add Savings Goal
                    </button>
                    <button
                      onClick={() => setShowAddSaving(false)}
                      className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Savings List with EDIT */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-blue-50 border-b border-blue-200">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-bold text-blue-900">Savings Category</th>
                      <th className="text-left px-6 py-4 text-sm font-bold text-blue-900">Description</th>
                      <th className="text-right px-6 py-4 text-sm font-bold text-blue-900">Monthly Amount</th>
                      <th className="text-right px-6 py-4 text-sm font-bold text-blue-900">% of Revenue</th>
                      <th className="text-right px-6 py-4 text-sm font-bold text-blue-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSavingsRecords()?.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                          No savings goals yet. Add your first savings goal above!
                        </td>
                      </tr>
                    ) : (
                      getSavingsRecords()?.map((record) => (
                        <tr key={record?.id} className="border-b border-slate-100 hover:bg-blue-50">
                          {editingRecord === record?.id ? (
                            <>
                              <td className="px-6 py-4 font-semibold text-slate-400">{record?.budgetCategories?.name}</td>
                              <td className="px-6 py-4">
                                <input
                                  type="text"
                                  value={editForm?.description}
                                  onChange={(e) => setEditForm({ ...editForm, description: e?.target?.value })}
                                  className="w-full px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500"
                                  placeholder="Description"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <input
                                  type="number"
                                  value={editForm?.amount}
                                  onChange={(e) => setEditForm({ ...editForm, amount: e?.target?.value })}
                                  className="w-full px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 text-right"
                                />
                              </td>
                              <td className="px-6 py-4 text-right text-slate-600">
                                {metrics?.totalRevenue > 0 
                                  ? ((parseFloat(editForm?.amount) / metrics?.totalRevenue) * 100)?.toFixed(1)
                                  : '0.0'}%
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => handleSaveEdit(record?.id)}
                                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="px-3 py-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300 text-sm"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4 font-semibold text-slate-900">{record?.budgetCategories?.name}</td>
                              <td className="px-6 py-4 text-slate-600">{record?.description}</td>
                              <td className="px-6 py-4 text-right font-bold text-blue-700">
                                {accountInfo?.currency}{parseFloat(record?.amount)?.toLocaleString()}
                              </td>
                              <td className="px-6 py-4 text-right text-slate-600">
                                {metrics?.totalRevenue > 0 
                                  ? ((parseFloat(record?.amount) / metrics?.totalRevenue) * 100)?.toFixed(1)
                                  : '0.0'}%
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => handleStartEdit(record)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                    title="Edit"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => showDeleteConfirmation(record)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                    {getSavingsRecords()?.length > 0 && (
                      <tr className="bg-blue-50 border-t-2 border-blue-200">
                        <td className="px-6 py-4 font-bold text-blue-900" colSpan={2}>TOTAL SAVINGS</td>
                        <td className="px-6 py-4 text-right font-bold text-blue-900 text-xl">
                          {accountInfo?.currency}{metrics?.budgetedSavings?.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-blue-900">
                          {metrics?.savingsPercent?.toFixed(1)}%
                        </td>
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Savings Insights - UPDATED */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-200 p-6">
                <h3 className="text-lg font-bold text-blue-900 mb-4">💰 Savings Insights</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <h4 className="font-semibold text-blue-900 mb-2">Savings Rate</h4>
                    <p className="text-2xl font-bold text-blue-700 mb-1">{metrics?.savingsPercent?.toFixed(1)}%</p>
                    <p className="text-sm text-slate-700">
                      {metrics?.savingsPercent >= 15 
                        ? `✅ Excellent! Saving ${metrics?.savingsPercent?.toFixed(0)}% of revenue.` 
                        : metrics?.savingsPercent >= 10 
                          ? '👍 Good! Aim for 15%+ for financial security.' 
                          : '💡 Try to reach 10% savings rate minimum.'}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <h4 className="font-semibold text-blue-900 mb-2">Monthly Savings</h4>
                    <p className="text-2xl font-bold text-blue-700">
                      {accountInfo?.currency}{metrics?.budgetedSavings?.toLocaleString()}
                    </p>
                    <p className="text-sm text-slate-700">
                      💡 Set aside from net profit
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <h4 className="font-semibold text-blue-900 mb-2">Annual Savings</h4>
                    <p className="text-2xl font-bold text-blue-700">
                      {accountInfo?.currency}{(metrics?.budgetedSavings * 12)?.toLocaleString()}
                    </p>
                    <p className="text-sm text-slate-700">
                      Projected yearly savings growth
                    </p>
                  </div>
                </div>
              </div>

              {/* Savings Tips - UPDATED */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">💡 Savings Best Practices</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-blue-600 font-bold">1</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Savings Reduce Disposable Cash</h4>
                      <p className="text-sm text-slate-600">Money saved is not available for bonuses or discretionary spending - it's allocated for future security</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-blue-600 font-bold">2</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Emergency Fund First</h4>
                      <p className="text-sm text-slate-600">Build 3-6 months of operating expenses before other savings goals</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-blue-600 font-bold">3</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Pay Yourself First</h4>
                      <p className="text-sm text-slate-600">Set aside savings automatically from net profit before calculating disposable cash</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SALARIES TAB */}
          {activeTab === 'salaries' && (
            <div className="space-y-6">
              {/* Add Employee Button */}
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Salary Management</h2>
                  <p className="text-sm text-slate-600 mt-1">Manage employee salaries - automatically tracked as expenses</p>
                </div>
                <button
                  onClick={() => setShowAddEmployee(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add Employee
                </button>
              </div>

              {/* Add Employee Form */}
              {showAddEmployee && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                  <h3 className="font-bold text-purple-900 mb-4">Add New Employee</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Employee Name"
                      value={newEmployee?.name}
                      onChange={(e) => setNewEmployee({ ...newEmployee, name: e?.target?.value })}
                      className="px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="Monthly Salary"
                      value={newEmployee?.salary || ''}
                      onChange={(e) => setNewEmployee({ ...newEmployee, salary: e?.target?.value })}
                      className="px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-purple-700 mt-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    This salary will be automatically added to your expenses and reduce your net profit
                  </p>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={handleAddEmployee}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
                    >
                      Add Employee & Expense
                    </button>
                    <button
                      onClick={() => setShowAddEmployee(false)}
                      className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Employee List - REMOVED Salary Increase and Bonus columns */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-purple-50 border-b border-purple-200">
                      <tr>
                        <th className="text-left px-6 py-4 text-sm font-bold text-purple-900">Employee Name</th>
                        <th className="text-right px-6 py-4 text-sm font-bold text-purple-900">Monthly Salary</th>
                        <th className="text-right px-6 py-4 text-sm font-bold text-purple-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSalaryExpenseRecords()?.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-6 py-8 text-center text-slate-500">
                            <div className="flex flex-col items-center gap-3">
                              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                                <Users className="w-8 h-8 text-purple-600" />
                              </div>
                              <p className="text-slate-600 font-medium">No employees yet.</p>
                              <p className="text-sm text-slate-500">Add your first employee above to start tracking salaries!</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        getSalaryExpenseRecords()?.map((salaryRecord) => (
                          <tr key={salaryRecord?.id} className="border-b border-slate-100 hover:bg-purple-50">
                            {editingRecord === salaryRecord?.id ? (
                              // EDIT MODE - Shows ONLY salary amount input (no sliders)
                              (<>
                                <td className="px-6 py-4 font-semibold text-slate-900">
                                  {salaryRecord?.budgetCategories?.name?.replace('Salary - ', '')}
                                </td>
                                <td className="px-6 py-4">
                                  <input
                                    type="number"
                                    value={editForm?.amount}
                                    onChange={(e) => setEditForm({ ...editForm, amount: e?.target?.value })}
                                    className="w-full px-3 py-2 border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-right transition-all duration-200"
                                    placeholder="Enter new salary"
                                  />
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <div className="flex justify-end gap-2">
                                    <button
                                      onClick={() => handleSaveEdit(salaryRecord?.id)}
                                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={handleCancelEdit}
                                      className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 text-sm font-semibold transition-all duration-200"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </td>
                              </>)
                            ) : (
                              // VIEW MODE - Shows ONLY salary amount
                              (<>
                                <td className="px-6 py-4 font-semibold text-slate-900">
                                  {salaryRecord?.budgetCategories?.name?.replace('Salary - ', '')}
                                </td>
                                <td className="px-6 py-4 text-right font-bold text-purple-700 text-lg">
                                  {accountInfo?.currency}{parseFloat(salaryRecord?.amount)?.toLocaleString()}
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <div className="flex justify-end gap-2">
                                    <button
                                      onClick={() => handleStartEdit(salaryRecord)}
                                      className="p-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors hover:scale-110"
                                      title="Edit Salary"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteEmployee(salaryRecord?.id)}
                                      className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors hover:scale-110"
                                      title="Delete Employee"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </>)
                            )}
                          </tr>
                        ))
                      )}
                      {getSalaryExpenseRecords()?.length > 0 && (
                        <tr className="bg-purple-50 border-t-2 border-purple-200">
                          <td className="px-6 py-4 font-bold text-purple-900">TOTAL MONTHLY SALARIES</td>
                          <td className="px-6 py-4 text-right font-bold text-purple-900 text-xl">
                            {accountInfo?.currency}{calculateTotalSalaryCost()?.toLocaleString()}
                          </td>
                          <td></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Salary Insights - REMOVED Bonuses card */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg shadow-sm border border-purple-200 p-6">
                <h3 className="text-lg font-bold text-purple-900 mb-4">💼 Salary Insights</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-purple-100">
                    <h4 className="font-semibold text-purple-900 mb-2">Total Employees</h4>
                    <p className="text-3xl font-bold text-purple-700">{getSalaryExpenseRecords()?.length}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-purple-100">
                    <h4 className="font-semibold text-purple-900 mb-2">Monthly Salary Cost</h4>
                    <p className="text-2xl font-bold text-purple-700">
                      {accountInfo?.currency}{calculateTotalSalaryCost()?.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-600 mt-1">Included in expenses</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-purple-100">
                    <h4 className="font-semibold text-purple-900 mb-2">Salary to Revenue</h4>
                    <p className="text-2xl font-bold text-purple-700">
                      {metrics?.totalRevenue > 0 
                        ? ((calculateTotalSalaryCost() / metrics?.totalRevenue) * 100)?.toFixed(1)
                        : '0.0'}%
                    </p>
                    <p className="text-xs text-slate-600 mt-1">
                      {metrics?.totalRevenue > 0 && ((calculateTotalSalaryCost() / metrics?.totalRevenue) * 100) <= 30
                        ? '✅ Healthy ratio' :'💡 Target: 20-30%'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Salary Tips - UPDATED to remove bonus mention */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">💡 Salary Management Tips</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-purple-600 font-bold">1</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Salaries Are Expenses</h4>
                      <p className="text-sm text-slate-600">All employee salaries are automatically counted as expenses and reduce your net profit</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-purple-600 font-bold">2</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Check Expenses Tab</h4>
                      <p className="text-sm text-slate-600">View all salaries in the Expenses tab - they're categorized with "Salary - [Employee Name]"</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-purple-600 font-bold">3</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Simple Salary Tracking</h4>
                      <p className="text-sm text-slate-600">Click the edit button to adjust an employee's salary. Changes are saved to the database immediately.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SETTINGS TAB */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              {/* Budget Planning Settings */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Budget Planning</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Savings Target (% of revenue)
                    </label>
                    <input
                      type="number"
                      value={budgetSettings?.savingsTarget}
                      onChange={(e) => {
                        setBudgetSettings({ ...budgetSettings, savingsTarget: parseFloat(e?.target?.value) || 0 });
                        calculateMetrics(records);
                      }}
                      step="0.5"
                      min="0"
                      max="50"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Current: {budgetSettings?.savingsTarget}% = {accountInfo?.currency}{metrics?.budgetedSavings?.toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Annual Salary Increase (%)
                    </label>
                    <input
                      type="number"
                      value={budgetSettings?.annualSalaryIncrease}
                      onChange={(e) => setBudgetSettings({ ...budgetSettings, annualSalaryIncrease: parseFloat(e?.target?.value) || 0 })}
                      step="0.5"
                      min="0"
                      max="20"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-slate-500 mt-1">Industry standard: 3-7% annually</p>
                  </div>
                </div>
              </div>

              {/* NEW: Developer Settings */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Developer Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-1">
                        Show Debug Tools
                      </label>
                      <p className="text-xs text-slate-600">
                        Display debug button in Income tab for viewing all database records
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={budgetSettings?.showDebugTools}
                        onChange={(e) => setBudgetSettings({ ...budgetSettings, showDebugTools: e?.target?.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Budget Tips - Enhanced */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-200 p-6">
                <h3 className="text-lg font-bold text-blue-900 mb-4">💡 Budget Planning Best Practices</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <h4 className="font-semibold text-blue-900 mb-2">📊 Track Everything</h4>
                    <p className="text-sm text-slate-700">
                      Record all income (both recurring and variable) and expenses consistently for accurate budget planning
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <h4 className="font-semibold text-blue-900 mb-2">💰 Build Emergency Fund</h4>
                    <p className="text-sm text-slate-700">
                      Aim to save 3-6 months of operating expenses for unexpected situations
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <h4 className="font-semibold text-blue-900 mb-2">🎯 Review Regularly</h4>
                    <p className="text-sm text-slate-700">
                      Check your budget monthly and adjust as your business grows and changes
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <h4 className="font-semibold text-blue-900 mb-2">📈 Average Income</h4>
                    <p className="text-sm text-slate-700">
                      Use average income for variable sources like sales. Update monthly based on actual performance
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <h4 className="font-semibold text-blue-900 mb-2">💼 Bonus Strategy</h4>
                    <p className="text-sm text-slate-700">
                      Remember: bonuses are one-off payments, not part of ongoing salary costs
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <h4 className="font-semibold text-blue-900 mb-2">🔄 Separate Savings</h4>
                    <p className="text-sm text-slate-700">
                      Set up dedicated savings goals in the Savings tab for better planning
                    </p>
                  </div>
                </div>
              </div>

              {/* Save Settings Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleManualSave}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  Save All Settings
                </button>
              </div>
            </div>
          )}

          {/* YEARLY VIEW TAB */}
          {activeTab === 'yearly' && (
            <YearlyBudgetView 
              accountInfo={accountInfo}
              user={user}
            />
          )}
        </main>
      </div>
    </>
  );
}

// NEW COMPONENT: Yearly Budget View
function YearlyBudgetView({ accountInfo, user }) {
  const [selectedYear, setSelectedYear] = useState(new Date()?.getFullYear());
  const [yearlyData, setYearlyData] = useState([]);
  const [yearlyTotals, setYearlyTotals] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadYearlyData();
    }
  }, [user, selectedYear]);

  const loadYearlyData = async () => {
    try {
      setIsLoading(true);
      const [monthlyData, totals, yoyComparison] = await Promise.all([
        budgetService?.getYearlyData(selectedYear),
        budgetService?.getYearlyTotals(selectedYear),
        budgetService?.getYearOverYearComparison(selectedYear, selectedYear - 1)
      ]);

      setYearlyData(monthlyData);
      setYearlyTotals(totals);
      setComparison(yoyComparison);
    } catch (err) {
      console.error('Error loading yearly data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate year options (last 5 years + current + next year)
  const yearOptions = [];
  const currentYear = new Date()?.getFullYear();
  for (let i = currentYear - 5; i <= currentYear + 1; i++) {
    yearOptions?.push(i);
  }

  const formatMonth = (monthStr) => {
    const date = new Date(monthStr + '-15');
    return date?.toLocaleDateString('en-US', { month: 'short' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Year Selector */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              Select Year
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e?.target?.value))}
              className="px-4 py-2.5 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 font-medium"
            >
              {yearOptions?.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-600 font-medium">Viewing Full Year:</p>
            <p className="text-2xl font-bold text-slate-900">{selectedYear}</p>
          </div>
        </div>
      </div>

      {/* Yearly Totals Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl shadow-lg border border-green-200 p-6">
          <h3 className="text-sm font-bold text-green-900 mb-2">Total Income ({selectedYear})</h3>
          <p className="text-3xl font-bold text-green-700 mb-1">
            {accountInfo?.currency}{yearlyTotals?.totalIncome?.toLocaleString()}
          </p>
          <p className="text-xs text-green-700">
            Avg: {accountInfo?.currency}{yearlyTotals?.averageMonthlyIncome?.toLocaleString()}/mo
          </p>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl shadow-lg border border-red-200 p-6">
          <h3 className="text-sm font-bold text-red-900 mb-2">Total Expenses ({selectedYear})</h3>
          <p className="text-3xl font-bold text-red-700 mb-1">
            {accountInfo?.currency}{yearlyTotals?.totalExpenses?.toLocaleString()}
          </p>
          <p className="text-xs text-red-700">
            Avg: {accountInfo?.currency}{yearlyTotals?.averageMonthlyExpenses?.toLocaleString()}/mo
          </p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-lg border border-blue-200 p-6">
          <h3 className="text-sm font-bold text-blue-900 mb-2">Total Savings ({selectedYear})</h3>
          <p className="text-3xl font-bold text-blue-700 mb-1">
            {accountInfo?.currency}{yearlyTotals?.totalSavings?.toLocaleString()}
          </p>
          <p className="text-xs text-blue-700">
            {yearlyTotals?.totalIncome > 0 
              ? ((yearlyTotals?.totalSavings / yearlyTotals?.totalIncome) * 100)?.toFixed(1)
              : 0}% of income
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl shadow-lg border border-purple-200 p-6">
          <h3 className="text-sm font-bold text-purple-900 mb-2">Net Profit ({selectedYear})</h3>
          <p className="text-3xl font-bold text-purple-700 mb-1">
            {accountInfo?.currency}{yearlyTotals?.netProfit?.toLocaleString()}
          </p>
          <p className="text-xs text-purple-700">
            {yearlyTotals?.totalIncome > 0 
              ? ((yearlyTotals?.netProfit / yearlyTotals?.totalIncome) * 100)?.toFixed(1)
              : 0}% margin
          </p>
        </div>
      </div>

      {/* Year-over-Year Comparison */}
      {comparison && (
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Year-over-Year Growth</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
              <p className="text-sm text-green-900 font-semibold mb-1">Income Growth</p>
              <p className={`text-2xl font-bold ${comparison?.growth?.income >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {comparison?.growth?.income >= 0 ? '+' : ''}{comparison?.growth?.income?.toFixed(1)}%
              </p>
              <p className="text-xs text-slate-600 mt-2">
                {selectedYear - 1}: {accountInfo?.currency}{comparison?.previousYear?.totalIncome?.toLocaleString()}
                <br />
                {selectedYear}: {accountInfo?.currency}{comparison?.currentYear?.totalIncome?.toLocaleString()}
              </p>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
              <p className="text-sm text-red-900 font-semibold mb-1">Expense Growth</p>
              <p className={`text-2xl font-bold ${comparison?.growth?.expenses <= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {comparison?.growth?.expenses >= 0 ? '+' : ''}{comparison?.growth?.expenses?.toFixed(1)}%
              </p>
              <p className="text-xs text-slate-600 mt-2">
                {selectedYear - 1}: {accountInfo?.currency}{comparison?.previousYear?.totalExpenses?.toLocaleString()}
                <br />
                {selectedYear}: {accountInfo?.currency}{comparison?.currentYear?.totalExpenses?.toLocaleString()}
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
              <p className="text-sm text-purple-900 font-semibold mb-1">Profit Growth</p>
              <p className={`text-2xl font-bold ${comparison?.growth?.profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {comparison?.growth?.profit >= 0 ? '+' : ''}{comparison?.growth?.profit?.toFixed(1)}%
              </p>
              <p className="text-xs text-slate-600 mt-2">
                {selectedYear - 1}: {accountInfo?.currency}{comparison?.previousYear?.netProfit?.toLocaleString()}
                <br />
                {selectedYear}: {accountInfo?.currency}{comparison?.currentYear?.netProfit?.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Trend Line Chart */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Monthly Trend ({selectedYear})</h3>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={yearlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="month" 
              tickFormatter={formatMonth}
              stroke="#64748b"
            />
            <YAxis stroke="#64748b" />
            <Tooltip 
              formatter={(value) => `${accountInfo?.currency}${value?.toLocaleString()}`}
              labelFormatter={formatMonth}
            />
            <Legend />
            <Line type="monotone" dataKey="income" stroke="#10b981" strokeWidth={3} name="Income" />
            <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={3} name="Expenses" />
            <Line type="monotone" dataKey="netProfit" stroke="#8b5cf6" strokeWidth={3} name="Net Profit" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly Bar Chart Comparison */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Income vs Expenses by Month</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={yearlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="month" 
              tickFormatter={formatMonth}
              stroke="#64748b"
            />
            <YAxis stroke="#64748b" />
            <Tooltip 
              formatter={(value) => `${accountInfo?.currency}${value?.toLocaleString()}`}
              labelFormatter={formatMonth}
            />
            <Legend />
            <Bar dataKey="income" fill="#10b981" name="Income" />
            <Bar dataKey="expenses" fill="#ef4444" name="Expenses" />
            <Bar dataKey="savings" fill="#3b82f6" name="Savings" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Yearly Insights */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-lg border border-blue-200 p-6">
        <h3 className="text-lg font-bold text-blue-900 mb-4">💡 Yearly Budget Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-5 border border-blue-100">
            <h4 className="font-semibold text-blue-900 mb-2">Best Month</h4>
            <p className="text-sm text-slate-700">
              {yearlyData?.length > 0 && (() => {
                const bestMonth = yearlyData?.reduce((prev, current) => 
                  (current?.netProfit > prev?.netProfit) ? current : prev
                );
                return `${formatMonth(bestMonth?.month)}: ${accountInfo?.currency}${bestMonth?.netProfit?.toLocaleString()} profit`;
              })()}
            </p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-blue-100">
            <h4 className="font-semibold text-blue-900 mb-2">Planning Next Year</h4>
            <p className="text-sm text-slate-700">
              {comparison?.growth?.income > 0 
                ? `✅ Income growing ${comparison?.growth?.income?.toFixed(1)}% - plan for ${(selectedYear + 1)}`
                : '💡 Focus on increasing revenue streams for growth'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}